import {
  type Announcements,
  type CollisionDetection,
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useTranslation } from '@hbs-us/ui';
import { useMemo, useState } from 'react';

import { AssignmentDetailPopover } from './AssignmentDetailPopover.js';
import type { DispatchAssignmentDto, DispatchJobDto } from './contractsMirror.js';
import {
  blockPosition,
  countOutsideRange,
  FULL_DAY,
  formatHourLabel,
  formatSpanLabel,
  type HourRange,
  hourMarks,
  layoutDay,
  minutesToHhMm,
  type PlacedBlock,
  slotStarts,
  WORKING_HOURS,
} from './timeGrid.js';
import { UnscheduledRail } from './UnscheduledRail.js';

/**
 * The accessible week board, drawn as a time grid.
 *
 * Hours run down the left, the seven Monday-start days across the top, and every assignment is a
 * block sitting at its start time and as tall as it runs. The board previously stacked cards in a
 * day column in list order with the time as a line of small text, which meant a nine-hour job and a
 * one-hour job were the same size, a 7am job and a 6pm job sat in the same place, and two crews on
 * the same hour were indistinguishable from two crews twelve hours apart. All of that information
 * was already in the data (`startTime`/`endTime` on every assignment); none of it was drawn.
 *
 * The geometry — spans, overlap lanes, clamping, the visible window — lives in `timeGrid.ts` and is
 * unit-tested. This file is the rendering and the drag wiring only.
 *
 * A11Y-01 — load-bearing, and unchanged by the redraw:
 *   • BOTH a `PointerSensor` (activationConstraint distance:4) AND a `KeyboardSensor`
 *     (coordinateGetter: sortableKeyboardCoordinates) are wired, so the board is operable with a
 *     pointer OR the keyboard (space/enter pick up, arrows move, esc cancel).
 *   • `accessibility.announcements` (t()-keyed EN/ES) announce drag start/over/end/cancel via the
 *     off-screen aria-live region dnd-kit renders by default, and now name the TIME as well as the
 *     day, because a drop target is a half-hour slot rather than a whole column.
 *   • EVERY job card ALSO has a non-drag "Schedule…" button (the `UnscheduledRail` fallback) that
 *     opens the focus-trapped `ScheduleFallbackDialog` — so a drag is never the ONLY way to assign.
 *   • Category colour is never the only carrier of meaning: the category is named in text on the
 *     block and again in the detail panel.
 *
 * Dropping on a slot assigns (or reschedules) to that day AND that time — a grid where a block
 * dropped at 2pm silently landed at 8am would be worse than no grid at all.
 */

/** Row height for one hour. Fourteen working hours at this height fit a laptop without scrolling. */
const HOUR_PX = 56;

/** The gutter + seven equal day columns. */
const GRID_COLS = 'grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))]';

/** `slot|2026-09-09|14:30` — the date and the clock string the assign mutation takes, verbatim. */
function slotId(date: string, min: number): string {
  return `slot|${date}|${minutesToHhMm(min)}`;
}

function parseSlotId(id: string): { date: string; startTime: string } | null {
  const parts = id.split('|');
  if (parts.length !== 3 || parts[0] !== 'slot') return null;
  return { date: parts[1], startTime: parts[2] };
}

function dayLabel(date: string): string {
  // YYYY-MM-DD → a stable short weekday + day-of-month. `Intl` honors the active <html lang>.
  const d = new Date(`${date}T00:00:00Z`);
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** One half-hour drop target. Invisible until something is dragged over it. */
function TimeSlot({ date, min, range }: { date: string; min: number; range: HourRange }) {
  const { setNodeRef, isOver } = useDroppable({ id: slotId(date, min), data: { date, min } });
  const total = range.endMin - range.startMin;
  return (
    <div
      ref={setNodeRef}
      aria-hidden="true"
      className={`absolute inset-x-0 transition-colors ${isOver ? 'bg-primary/20 ring-1 ring-ring' : ''}`}
      style={{
        top: `${((min - range.startMin) / total) * 100}%`,
        height: `${(30 / total) * 100}%`,
      }}
    />
  );
}

interface BlockProps {
  block: PlacedBlock;
  range: HourRange;
  side: 'left' | 'right';
  isOpen: boolean;
  onToggle: (id: number | null) => void;
  onEditAssignment: (assignment: DispatchAssignmentDto) => void;
  onUnassignAssignment: (assignment: DispatchAssignmentDto) => void;
}

/**
 * One assignment, positioned and sized by its time.
 *
 * The block is both a drag handle (reschedule to another day/time) and the trigger for the detail
 * panel. Those coexist because the `PointerSensor` only starts a drag after 4px of movement, so a
 * stationary press is a click. Content is clipped rather than wrapped — a 30-minute block has room
 * for the time and a name, and everything else lives in the panel.
 */
function AssignmentBlock({
  block,
  range,
  side,
  isOpen,
  onToggle,
  onEditAssignment,
  onUnassignAssignment,
}: BlockProps) {
  const { t } = useTranslation();
  const { assignment } = block;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `assignment-${assignment.id}`,
  });
  const pos = blockPosition(block, range);
  const label = assignment.jobName ?? assignment.customerName ?? assignment.employeeName;
  const color = assignment.categoryColor;

  return (
    <div
      className="absolute px-px"
      style={{
        top: `${pos.topPct}%`,
        height: `${pos.heightPct}%`,
        left: `${pos.leftPct}%`,
        width: `${pos.widthPct}%`,
      }}
    >
      <button
        type="button"
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        onClick={() => onToggle(isOpen ? null : assignment.id)}
        aria-expanded={isOpen}
        aria-label={t('dispatch:board.blockLabel', {
          job: label,
          crew: assignment.employeeName,
          time: formatSpanLabel(block.startMin, block.endMin),
        })}
        className={`h-full w-full overflow-hidden rounded-sm border border-border/60 border-l-4 bg-card p-1 text-left transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          isDragging ? 'opacity-40' : ''
        } ${isOpen ? 'ring-2 ring-ring' : ''}`}
        style={{
          borderLeftColor: color ?? undefined,
          backgroundColor: color ? `color-mix(in srgb, ${color} 12%, transparent)` : undefined,
        }}
      >
        <span className="block truncate text-[0.65rem] text-muted-foreground">
          {formatSpanLabel(block.startMin, block.endMin)}
        </span>
        <span className="block truncate font-medium text-foreground text-xs">{label}</span>
        <span className="block truncate text-[0.65rem] text-muted-foreground">
          {assignment.employeeName}
        </span>
        {assignment.categoryName ? (
          <span className="block truncate text-[0.65rem] text-muted-foreground">
            {assignment.categoryName}
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <AssignmentDetailPopover
          assignment={assignment}
          side={side}
          onClose={() => onToggle(null)}
          onEdit={(a) => {
            onToggle(null);
            onEditAssignment(a);
          }}
          onUnassign={(a) => {
            onToggle(null);
            onUnassignAssignment(a);
          }}
        />
      ) : null}
    </div>
  );
}

interface DayColumnProps {
  date: string;
  dayIndex: number;
  isToday: boolean;
  assignments: DispatchAssignmentDto[];
  range: HourRange;
  openId: number | null;
  onToggleOpen: (id: number | null) => void;
  onEditAssignment: (assignment: DispatchAssignmentDto) => void;
  onUnassignAssignment: (assignment: DispatchAssignmentDto) => void;
}

function DayColumn({
  date,
  dayIndex,
  isToday,
  assignments,
  range,
  openId,
  onToggleOpen,
  onEditAssignment,
  onUnassignAssignment,
}: DayColumnProps) {
  const { t } = useTranslation();
  const { placed } = useMemo(() => layoutDay(assignments), [assignments]);
  const visible = placed.filter((b) => blockPosition(b, range).heightPct > 0);

  return (
    // biome-ignore lint/a11y/useSemanticElements: a labelled role="group" is the correct region for one day of the grid; a <fieldset> implies form controls (there are none) and would force unwanted default styling.
    <div
      role="group"
      aria-label={t('dispatch:board.dayColumn', { day: dayLabel(date) })}
      className={`relative border-border border-r border-b last:border-r-0 ${
        isToday ? 'bg-primary/5' : ''
      }`}
      style={{ height: ((range.endMin - range.startMin) / 60) * HOUR_PX }}
    >
      {/* The hour rulings. Purely visual — the gutter carries the readable labels. */}
      {hourMarks(range).map((min) => (
        <div
          key={min}
          aria-hidden="true"
          className="absolute inset-x-0 border-border/50 border-t"
          style={{ top: `${((min - range.startMin) / (range.endMin - range.startMin)) * 100}%` }}
        />
      ))}

      {slotStarts(range).map((min) => (
        <TimeSlot key={min} date={date} min={min} range={range} />
      ))}

      {visible.map((block) => (
        <AssignmentBlock
          key={block.assignment.id}
          block={block}
          range={range}
          // The last two columns open their panel leftward so it stays on the board.
          side={dayIndex >= 5 ? 'left' : 'right'}
          isOpen={openId === block.assignment.id}
          onToggle={onToggleOpen}
          onEditAssignment={onEditAssignment}
          onUnassignAssignment={onUnassignAssignment}
        />
      ))}
    </div>
  );
}

export function DispatchBoard({
  days,
  today,
  jobs,
  assignments,
  onScheduleJob,
  onAssignJobToSlot,
  onRescheduleToSlot,
  onEditAssignment,
  onUnassignAssignment,
}: {
  days: string[];
  today: string;
  jobs: DispatchJobDto[];
  assignments: DispatchAssignmentDto[];
  /** Open the non-drag fallback dialog for a job (the keyboard path). */
  onScheduleJob: (job: DispatchJobDto) => void;
  /** A job card was dropped onto a slot → create an assignment at that day and time. */
  onAssignJobToSlot: (jobId: number, date: string, startTime: string) => void;
  /** A scheduled block was dropped onto a slot → reschedule to that day and time. */
  onRescheduleToSlot: (assignmentId: number, date: string, startTime: string) => void;
  /** Open the full-field edit dialog for an assignment. */
  onEditAssignment: (assignment: DispatchAssignmentDto) => void;
  /** Open the destructive unassign confirm for an assignment. */
  onUnassignAssignment: (assignment: DispatchAssignmentDto) => void;
}) {
  const { t } = useTranslation();
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);

  const range = expanded ? FULL_DAY : WORKING_HOURS;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Half-hour slots are small and numerous, so the slot under the POINTER should win; rect overlap
  // alone picks a neighbour as often as not. `closestCenter` is the fallback the keyboard sensor
  // needs, since a keyboard drag has no pointer at all.
  const collisionDetection: CollisionDetection = (args) => {
    const byPointer = pointerWithin(args);
    return byPointer.length > 0 ? byPointer : closestCenter(args);
  };

  const byDay = useMemo(() => {
    const map = new Map<string, DispatchAssignmentDto[]>(days.map((d) => [d, []]));
    for (const a of assignments) map.get(a.scheduledDate)?.push(a);
    return map;
  }, [days, assignments]);

  // Untimed assignments have no place on a time grid, and dropping them silently would be a data
  // loss the dispatcher could not see. They get a strip above it.
  const untimedByDay = useMemo(() => {
    const map = new Map<string, DispatchAssignmentDto[]>();
    for (const date of days) map.set(date, layoutDay(byDay.get(date) ?? []).untimed);
    return map;
  }, [days, byDay]);

  const hiddenCount = useMemo(() => {
    let total = 0;
    for (const date of days)
      total += countOutsideRange(layoutDay(byDay.get(date) ?? []).placed, range);
    return total;
  }, [days, byDay, range]);

  const jobNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const j of jobs) map.set(j.id, j.jobName);
    return map;
  }, [jobs]);

  function labelForActiveId(id: string): string {
    if (id.startsWith('job-')) return jobNameById.get(Number(id.slice(4))) ?? id;
    const assignmentId = Number(id.replace('assignment-', ''));
    const match = assignments.find((a) => a.id === assignmentId);
    return match?.jobName ?? match?.employeeName ?? id;
  }

  /** "Tue, Sep 8 at 2:30 PM" for the aria-live announcements. */
  function slotLabel(id: string): string {
    const slot = parseSlotId(id);
    if (!slot) return id;
    const [h, m] = slot.startTime.split(':').map(Number);
    return t('dispatch:a11y.slot', {
      day: dayLabel(slot.date),
      time: formatHourLabel(h * 60 + m),
    });
  }

  // t()-keyed EN/ES announcements (I18N-05) over dnd-kit's default off-screen aria-live region.
  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      t('dispatch:a11y.pickedUp', { job: labelForActiveId(String(active.id)) }),
    onDragOver: ({ active, over }) =>
      over
        ? t('dispatch:a11y.over', {
            job: labelForActiveId(String(active.id)),
            day: slotLabel(String(over.id)),
          })
        : t('dispatch:a11y.noTarget'),
    onDragEnd: ({ active, over }) =>
      over
        ? t('dispatch:a11y.dropped', {
            job: labelForActiveId(String(active.id)),
            day: slotLabel(String(over.id)),
          })
        : t('dispatch:a11y.cancelled', { job: labelForActiveId(String(active.id)) }),
    onDragCancel: ({ active }) =>
      t('dispatch:a11y.cancelled', { job: labelForActiveId(String(active.id)) }),
  };

  function handleDragStart(event: DragStartEvent) {
    setOpenId(null);
    setActiveLabel(labelForActiveId(String(event.active.id)));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveLabel(null);
    const { active, over } = event;
    if (!over) return;
    const slot = parseSlotId(String(over.id));
    if (!slot) return;
    const activeId = String(active.id);
    if (activeId.startsWith('job-')) {
      onAssignJobToSlot(Number(activeId.slice(4)), slot.date, slot.startTime);
    } else if (activeId.startsWith('assignment-')) {
      onRescheduleToSlot(Number(activeId.slice(11)), slot.date, slot.startTime);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      accessibility={{
        announcements,
        screenReaderInstructions: { draggable: t('dispatch:a11y.instructions') },
      }}
      onDragStart={handleDragStart}
      onDragCancel={() => setActiveLabel(null)}
      onDragEnd={handleDragEnd}
    >
      <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
        <UnscheduledRail jobs={jobs} onSchedule={onScheduleJob} />

        {/* biome-ignore lint/a11y/useSemanticElements: a labelled `role="group"` wrapping the week
            grid is the correct grouping landmark; <fieldset> implies form controls (there are
            none) and would force unwanted default styling. */}
        <div role="group" aria-label={t('dispatch:board.weekLabel')} className="min-w-0">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p aria-live="polite" className="text-muted-foreground text-xs">
              {hiddenCount > 0 ? t('dispatch:board.outsideHours', { count: hiddenCount }) : null}
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex h-8 items-center rounded-sm bg-secondary px-2 font-medium text-[0.7rem] text-secondary-foreground transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {expanded ? t('dispatch:board.showWorkingHours') : t('dispatch:board.showFullDay')}
            </button>
          </div>

          {/* Day headers. */}
          <div className={GRID_COLS}>
            <div className="border-border border-b" />
            {days.map((date) => (
              <div
                key={date}
                className={`border-border border-r border-b px-1 py-1 text-center last:border-r-0 ${
                  date === today ? 'bg-primary/10' : ''
                }`}
              >
                <span
                  className={`font-medium text-xs ${date === today ? 'text-primary-ink' : 'text-foreground'}`}
                >
                  {dayLabel(date)}
                </span>
              </div>
            ))}
          </div>

          {/* The all-day strip — assignments with no usable start time. */}
          <div className={GRID_COLS}>
            <div className="border-border border-b px-1 py-1 text-right text-[0.65rem] text-muted-foreground">
              {t('dispatch:board.allDay')}
            </div>
            {days.map((date) => (
              <div
                key={date}
                className="flex min-h-8 flex-col gap-0.5 border-border border-r border-b p-0.5 last:border-r-0"
              >
                {(untimedByDay.get(date) ?? []).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => onEditAssignment(a)}
                    className="truncate rounded-sm border border-border/60 border-l-4 bg-card px-1 py-0.5 text-left text-[0.65rem] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    style={{ borderLeftColor: a.categoryColor ?? undefined }}
                  >
                    {a.jobName ?? a.employeeName} · {a.employeeName}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* The grid. Scrolls independently so the headers and the rail stay put. */}
          <div className="max-h-[70vh] overflow-y-auto overflow-x-visible">
            <div className={GRID_COLS}>
              <div
                className="relative border-border border-r"
                style={{ height: ((range.endMin - range.startMin) / 60) * HOUR_PX }}
              >
                {hourMarks(range).map((min) => (
                  /* Each label is centred on its line, except the first: centred, it hung half
                     its height above the scroller and was cut off. It sits just below its line.
                     (Padding the scroller instead left a blank seam above today's column.) */
                  <span
                    key={min}
                    className={`absolute right-1 text-[0.65rem] text-muted-foreground ${
                      min === range.startMin ? 'translate-y-0.5' : '-translate-y-1/2'
                    }`}
                    style={{
                      top: `${((min - range.startMin) / (range.endMin - range.startMin)) * 100}%`,
                    }}
                  >
                    {formatHourLabel(min)}
                  </span>
                ))}
              </div>

              {days.map((date, index) => (
                <DayColumn
                  key={date}
                  date={date}
                  dayIndex={index}
                  isToday={date === today}
                  assignments={byDay.get(date) ?? []}
                  range={range}
                  openId={openId}
                  onToggleOpen={setOpenId}
                  onEditAssignment={onEditAssignment}
                  onUnassignAssignment={onUnassignAssignment}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeLabel ? (
          <div className="rounded-md border border-ring bg-card p-3 shadow-lg">
            <p className="font-medium text-foreground text-sm">{activeLabel}</p>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
